import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  rootMargin?: string;
  threshold?: number;
}

/**
 * Hook personalizado para implementar infinite scroll usando IntersectionObserver
 * @param hasMore - Indica si hay más elementos por cargar
 * @param isLoading - Indica si se está cargando actualmente
 * @param onLoadMore - Función callback que se ejecuta cuando se debe cargar más contenido
 * @param rootMargin - Margen del root para el IntersectionObserver (default: '100px')
 * @param threshold - Umbral de visibilidad para activar la carga (default: 0.1)
 * @returns ref - Referencia que debe asignarse al elemento sentinel
 */
export const useInfiniteScroll = ({
  hasMore,
  isLoading,
  onLoadMore,
  rootMargin = '100px',
  threshold = 0.1
}: UseInfiniteScrollOptions) => {
  const sentinelRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      {
        root: null,
        rootMargin,
        threshold
      }
    );

    observer.observe(sentinel);

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, isLoading, loadMore, rootMargin, threshold]);

  return sentinelRef;
};

